/* Small pieces every attendance screen shares: how a time reads, the status
 * chip, and the numbered pager under a long list. */
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { AttendanceStatus } from '@/types/api'

export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

/** "9:12 am" */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "Sun 12 Oct" from "2026-10-12" — read as a local date, not UTC midnight,
 *  which would be the day before anywhere west of Greenwich. */
export function day(date: string, long = false): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: long ? 'long' : 'short',
    day: 'numeric',
    month: long ? 'long' : 'short',
  })
}

/** Today as "YYYY-MM-DD" in the browser's own calendar. */
export function todayIso(): string {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

/** "daughter" → "Daughter" */
export function relationLabel(relation: string | null | undefined): string {
  if (!relation) return 'Family'
  return relation.charAt(0).toUpperCase() + relation.slice(1).replace(/_/g, ' ')
}

/** "Anita's daughter" */
export function familyNote(householdOf: string | null | undefined, relation: string | null | undefined) {
  if (!householdOf) return null
  const first = householdOf.split(' ')[0]
  return `${first}'s ${relation ? relation.replace(/_/g, ' ') : 'family'}`
}

const STATUS: Record<AttendanceStatus | 'not_expected', { label: string; tone: string }> = {
  present: { label: 'Present', tone: 'bg-success-soft text-success-ink' },
  excused: { label: 'Excused', tone: 'bg-warning-soft text-warning-ink' },
  absent: { label: 'Absent', tone: 'bg-danger-soft text-danger' },
  pending: { label: 'Not yet', tone: 'bg-surface-2 text-ink-2' },
  not_expected: { label: 'Visitor', tone: 'bg-surface-2 text-ink-2' },
}

export function StatusChip({ status }: { status: AttendanceStatus | 'not_expected' }) {
  const s = STATUS[status]
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-pill px-s2 py-0.5 text-xs font-semibold',
        s.tone,
      )}
    >
      {s.label}
    </span>
  )
}

/** Page numbers, with Previous and Next either side. A long run collapses
 *  to the first, the last and the pages around this one. */
export function Pager({
  page,
  total,
  pageSize,
  onPage,
}: {
  page: number
  total: number
  pageSize: number
  onPage: (page: number) => void
}) {
  const last = Math.max(1, Math.ceil(total / pageSize))
  if (total <= pageSize) return null

  const pages: (number | '…')[] = []
  for (let p = 1; p <= last; p++) {
    if (p === 1 || p === last || Math.abs(p - page) <= 1) pages.push(p)
    else if (pages[pages.length - 1] !== '…') pages.push('…')
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-s2">
      <span className="text-sm text-ink-2">
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        <Button size="sm" onClick={() => onPage(page - 1)} disabled={page <= 1}>
          Previous
        </Button>
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="px-1 text-sm text-ink-3">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPage(p)}
              aria-current={p === page ? 'page' : undefined}
              className={cn(
                'h-9 min-w-[36px] rounded px-s2 text-sm font-semibold transition-colors',
                p === page ? 'bg-brand text-ink-on-brand' : 'text-ink-2 hover:bg-hover',
              )}
            >
              {p}
            </button>
          ),
        )}
        <Button size="sm" onClick={() => onPage(page + 1)} disabled={page >= last}>
          Next
        </Button>
      </div>
    </div>
  )
}

/** The underlined tab row the group screen uses. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { key: T; label: string }[]
  value: T
  onChange: (next: T) => void
}) {
  return (
    <div className="flex gap-s1 overflow-x-auto border-b border-line-2" role="tablist">
      {tabs.map(({ key, label }) => (
        <button
          key={key}
          role="tab"
          aria-selected={value === key}
          onClick={() => onChange(key)}
          className={cn(
            'relative shrink-0 px-s3 py-s3 text-md font-semibold transition-colors sm:px-s4',
            value === key ? 'text-brand' : 'text-ink-2 hover:text-ink',
          )}
        >
          {label}
          {value === key ? (
            <span className="absolute inset-x-0 -bottom-px h-[3px] rounded-t bg-brand" aria-hidden />
          ) : null}
        </button>
      ))}
    </div>
  )
}

/** "Search by name" — the pill input every list here uses. */
export function SearchBox({
  value,
  onChange,
  placeholder = 'Search by name',
  label,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  label: string
}) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className="w-full rounded-pill border border-line-2 bg-surface-2 px-s4 py-s2 text-base outline-none focus:shadow-focus"
    />
  )
}
